import React from 'react'
import { useQuery } from '@tanstack/react-query'
import { 
  Activity, 
  Database, 
  Shield, 
  AlertTriangle,
  TrendingUp,
  Clock,
  Rss,
  CheckCircle,
  XCircle
} from 'lucide-react'
import { api } from '../lib/api'

export default function DashboardPage() {
  const { data: health } = useQuery({
    queryKey: ['health'],
    queryFn: () => api.getHealth(),
    refetchInterval: 30000, // Refresh every 30 seconds
  })

  const { data: collections } = useQuery({
    queryKey: ['collections'],
    queryFn: () => api.getCollections(),
  })

  const { data: feeds } = useQuery({
    queryKey: ['feeds'],
    queryFn: () => api.getFeeds(),
    refetchInterval: 5000, // Refresh every 5 seconds
  })

  const { data: stats } = useQuery({
    queryKey: ['stats'],
    queryFn: () => api.getStats(),
    refetchInterval: 10000, // Refresh every 10 seconds
  })

  const activeFeedsCount = feeds?.feeds?.filter((feed: any) => feed.is_active).length || 0
  const indicatorObjects = stats?.stats?.recent_objects_by_type?.find((obj: any) => obj.stix_type === 'indicator')
  const totalIndicators = indicatorObjects?.count || 0
  const pendingPolls = feeds?.feeds?.filter((feed: any) => 
    feed.is_active && (!feed.last_poll_time || feed.last_poll_status === 'pending')
  ).length || 0

  const dashboardStats = [
    {
      name: 'Platform Status',
      value: health?.status === 'healthy' ? 'Operational' : 'Unknown',
      icon: Activity,
      color: health?.status === 'healthy' ? 'text-green-600' : 'text-gray-600',
      bgColor: health?.status === 'healthy' ? 'bg-green-100' : 'bg-gray-100',
    },
    {
      name: 'Collections',
      value: collections?.collections?.length || '0',
      icon: Database,
      color: 'text-blue-600',
      bgColor: 'bg-blue-100',
    },
    {
      name: 'Threat Indicators',
      value: totalIndicators > 0 ? totalIndicators.toLocaleString() : pendingPolls > 0 ? 'Polling...' : '0',
      icon: Shield,
      color: pendingPolls > 0 ? 'text-yellow-600' : totalIndicators > 0 ? 'text-purple-600' : 'text-gray-600',
      bgColor: pendingPolls > 0 ? 'bg-yellow-100' : totalIndicators > 0 ? 'bg-purple-100' : 'bg-gray-100',
    },
    {
      name: 'Active Feeds',
      value: activeFeedsCount.toString(),
      icon: TrendingUp,
      color: 'text-orange-600',
      bgColor: 'bg-orange-100',
    },
  ]

  return (
    <div className="p-6">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
        <p className="text-gray-600">
          Overview of your threat intelligence platform
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {dashboardStats.map((stat) => (
          <div key={stat.name} className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className={`${stat.bgColor} rounded-md p-3`}>
                <stat.icon className={`h-6 w-6 ${stat.color}`} />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">{stat.name}</p>
                <p className="text-2xl font-semibold text-gray-900">{stat.value}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg shadow">
          <div className="p-6 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">Active Feeds</h3>
          </div>
          <div className="p-6">
            <div className="space-y-4">
              <div className="flex items-center space-x-3">
                <Clock className="h-5 w-5 text-gray-400" />
                <div className="flex-1">
                  <p className="text-sm text-gray-900">Platform initialized</p>
                  <p className="text-xs text-gray-500">
                    {health?.timestamp ? new Date(health.timestamp).toLocaleString() : 'Unknown'}
                  </p>
                </div>
              </div>
              
              {feeds?.feeds?.slice(0, 3).map((feed: any) => (
                <div key={feed.id} className="flex items-center space-x-3">
                  <Rss className="h-5 w-5 text-blue-400" />
                  <div className="flex-1">
                    <p className="text-sm text-gray-900">Feed: {feed.name}</p>
                    <p className="text-xs text-gray-500 flex items-center space-x-2">
                      <span className={`inline-flex items-center ${
                        feed.is_active ? 'text-green-600' : 'text-gray-500'
                      }`}>
                        {feed.is_active ? <CheckCircle className="h-3 w-3 mr-1" /> : <XCircle className="h-3 w-3 mr-1" />}
                        {feed.is_active ? 'Active' : 'Inactive'}
                      </span>
                      {feed.last_poll_time && (
                        <span>• Last poll: {new Date(feed.last_poll_time).toLocaleDateString()}</span>
                      )}
                    </p>
                  </div>
                </div>
              ))}
              
              {(!feeds?.feeds || feeds.feeds.length === 0) && (
                <div className="text-center py-8">
                  <AlertTriangle className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                  <p className="text-sm text-gray-500">No feeds configured</p>
                  <p className="text-xs text-gray-400">Add your first feed to start collecting threat intelligence</p>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow">
          <div className="p-6 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">System Status</h3>
          </div>
          <div className="p-6">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Gateway Service</span>
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                  Healthy
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Auth Service</span>
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                  Healthy
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">TAXII Service</span>
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                  Healthy
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Enrichment Service</span>
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                  Healthy
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Poller Service</span>
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                  Healthy
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}